import { Input, Select, SelectOption } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocIconButton } from 'commons/components/components-wrapper/poc-icon-button.component';
import { PocToggleButton } from 'commons/components/components-wrapper/poc-toggle-button.component';
import { PocToggleButtonGroup } from 'commons/components/components-wrapper/poc-toggle-button-group.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { toStringValue } from 'commons/utils/string.utils';
import { useTranslation } from 'core/i18n/i18n';
import {
    isRangeMode,
    isResultType,
    RangeMode,
    ResultType,
} from 'modules/settings/pods/settings-materials/material-definition.vm';
import { RANGE_MODE } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.constants';
import {
    DEFAULT_RANGE_MODE,
    MAX_QC_LEVELS_PER_TEST,
    nextLevelNumber,
    QcLevelEditableField,
    TestConfigFormItem,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import { QcLevelLine } from './qc-level-line.component';
import {
    QcConfigLevels,
    QcConfigRangeMode,
    QcConfigRow,
    QcConfigRowHeader,
    QcFieldLabel,
    QcTestFieldsRow,
} from './test-config-card.style';

const QC_CONFIG_KEY = 'settings.materials.material-definitions.form.qc-config';
const TEST_CONFIG_KEY = 'settings.materials.material-definitions.form.test-config';

interface TestConfigCardProps {
    testConfig: TestConfigFormItem;
    cardIndex: number;
    testName?: string;
    testOptions: SelectOption[];
    resultTypeOptions: SelectOption[];
    onRemoveTest: (clientKey: string) => void;
    onTestChange: (clientKey: string, testId: string) => void;
    onResultTypeChange: (clientKey: string, resultType: ResultType) => void;
    onPossibleValuesChange: (clientKey: string, possibleValues: string) => void;
    onRangeModeChange: (clientKey: string, rangeMode: RangeMode) => void;
    onAddLevel: (clientKey: string, defaultName: string) => void;
    onRemoveLevel: (clientKey: string, index: number) => void;
    onLevelChange: (clientKey: string, index: number, field: QcLevelEditableField, value: string | null) => void;
    targetErrors?: Record<number, string>;
}

export const TestConfigCard: React.FC<TestConfigCardProps> = ({
    testConfig,
    cardIndex,
    testName,
    testOptions,
    resultTypeOptions,
    onRemoveTest,
    onTestChange,
    onResultTypeChange,
    onPossibleValuesChange,
    onRangeModeChange,
    onAddLevel,
    onRemoveLevel,
    onLevelChange,
    targetErrors,
}) => {
    const { t } = useTranslation();

    const activeRangeMode = testConfig.rangeMode ?? DEFAULT_RANGE_MODE;
    const cardTitle = testName ?? t(`${TEST_CONFIG_KEY}.card-title-placeholder`);
    const removeTestAria = testConfig.testId
        ? t(`${TEST_CONFIG_KEY}.remove-test-aria`, { name: cardTitle })
        : t(`${TEST_CONFIG_KEY}.remove-test-aria-empty`, { position: cardIndex + 1 });

    return (
        <QcConfigRow>
            <QcConfigRowHeader>
                <PocTypography variant="subtitle2">{cardTitle}</PocTypography>
                <PocIconButton
                    icon="delete"
                    family="outlined"
                    aria-label={removeTestAria}
                    data-testid={`qc-test-remove-${testConfig.clientKey}`}
                    onClick={() => onRemoveTest(testConfig.clientKey)}
                />
            </QcConfigRowHeader>
            <QcTestFieldsRow>
                <Select
                    name={`test-${testConfig.clientKey}`}
                    label={`${TEST_CONFIG_KEY}.test-label`}
                    placeholder={`${TEST_CONFIG_KEY}.test-placeholder`}
                    options={testOptions}
                    value={testConfig.testId || undefined}
                    required
                    searchable
                    onChange={(_, value) => onTestChange(testConfig.clientKey, toStringValue(value))}
                />
                <Select
                    name={`result-type-${testConfig.clientKey}`}
                    label={`${TEST_CONFIG_KEY}.result-type-label`}
                    placeholder={`${TEST_CONFIG_KEY}.result-type-placeholder`}
                    options={resultTypeOptions}
                    value={testConfig.resultType}
                    required
                    onChange={(_, value) => {
                        const next = toStringValue(value);
                        if (isResultType(next)) {
                            onResultTypeChange(testConfig.clientKey, next);
                        }
                    }}
                />
            </QcTestFieldsRow>
            {testConfig.resultType === 'QUANTITATIVE' && (
                <QcConfigRangeMode>
                    <QcFieldLabel>{t(`${QC_CONFIG_KEY}.range-mode.label`)}</QcFieldLabel>
                    <PocToggleButtonGroup
                        exclusive
                        controlled
                        data-testid={`qc-config-range-mode-toggle-${testConfig.clientKey}`}
                        value={[activeRangeMode]}
                        onValueChange={(event) => {
                            const next = toStringValue(event.detail);
                            if (isRangeMode(next)) {
                                onRangeModeChange(testConfig.clientKey, next);
                            }
                        }}
                    >
                        <PocToggleButton value={RANGE_MODE.MIN_MAX}>
                            {t(`${QC_CONFIG_KEY}.range-mode.min-max`)}
                        </PocToggleButton>
                        <PocToggleButton value={RANGE_MODE.TARGET_DEVIATION}>
                            {t(`${QC_CONFIG_KEY}.range-mode.target-deviation`)}
                        </PocToggleButton>
                    </PocToggleButtonGroup>
                </QcConfigRangeMode>
            )}
            {testConfig.resultType === 'QUALITATIVE' && (
                <Input
                    name={`possible-values-${testConfig.clientKey}`}
                    label={`${TEST_CONFIG_KEY}.possible-values-label`}
                    ariaLabel={t(`${TEST_CONFIG_KEY}.possible-values-aria`, { name: cardTitle })}
                    placeholder={`${TEST_CONFIG_KEY}.possible-values-placeholder`}
                    assistiveText={`${TEST_CONFIG_KEY}.possible-values-hint`}
                    pattern=".*"
                    value={testConfig.possibleValues ?? ''}
                    onChange={(_, value) => onPossibleValuesChange(testConfig.clientKey, value ?? '')}
                />
            )}
            <QcConfigLevels>
                {(testConfig.qcLevels ?? []).map((level, index) => (
                    <QcLevelLine
                        key={level.clientKey ?? level.ordinal}
                        level={level}
                        index={index}
                        rowKey={testConfig.clientKey}
                        resultType={testConfig.resultType}
                        rangeMode={activeRangeMode}
                        errorKey={targetErrors?.[index]}
                        onChange={(field, value) => onLevelChange(testConfig.clientKey, index, field, value)}
                        onRemove={() => onRemoveLevel(testConfig.clientKey, index)}
                    />
                ))}
            </QcConfigLevels>
            <PocButton
                variant="tertiary"
                data-testid={`qc-level-add-${testConfig.clientKey}`}
                disabled={(testConfig.qcLevels?.length ?? 0) >= MAX_QC_LEVELS_PER_TEST}
                onClick={() =>
                    onAddLevel(
                        testConfig.clientKey,
                        t(`${QC_CONFIG_KEY}.levels.default-name`, {
                            n: nextLevelNumber(testConfig.qcLevels ?? []),
                        })
                    )
                }
            >
                <PocIcon slot="prefix" name="sign_plus" />
                {t(`${QC_CONFIG_KEY}.levels.add`)}
            </PocButton>
        </QcConfigRow>
    );
};
